// @ts-nocheck
import React from 'react';
import { createPortal } from 'react-dom';
import PlacesAutocomplete from 'react-places-autocomplete';

import * as styles from './styles';
import { cn } from '@cobliteam/ui/utils';

import { Analytics } from '@cobliteam/analytics';

import { LocationIcon } from '../../icons';

import { HintPopup } from '../../HintPopup/HintPopup';
import { DefaultInput, type InputProps } from '../DefaultInput';

const AUTOCOMPLETE_DEBOUNCE_TIME_IN_MILLIS = 500;
const MIN_ADDRESS_LENGTH_TO_AUTOCOMPLETE = 3;

export interface PlacesAutocompleteInputProps extends InputProps {
  value?: string;
  error?: boolean;
  portal?: boolean;
  enableHintPopup?: boolean;
  onAddressChange?: (address: string) => void;
  onAddressSelect?: (address: string) => void;
  customAutoCompleteDebounceTime?: number;
  alwaysFetchSuggestions?: boolean;
  bounds?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  googleCallbackName?: string;
  restrictions?: google.maps.GeocoderComponentRestrictions;
}

interface PlacesAutocompleteInputState {
  address: string;
}

const portalContainerId = 'places-autocomplete-portal-container';

// TODO: Make this component initialize the google global variable by itself, without relying on legacy to do so
/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export class PlacesAutocompleteInput extends React.Component<PlacesAutocompleteInputProps, PlacesAutocompleteInputState> {
  private timerHandler: NodeJS.Timer | null;
  private portalContainer: HTMLDivElement | null = null;

  constructor(props: PlacesAutocompleteInputProps) {
    super(props);
    this.timerHandler = null;
    this.state = { address: this.props.value || '' };
  }

  public componentDidMount() {
    if (!window.google) {
      this.timerHandler = setInterval(this.googleReadyCallback, 500);
    }
  }

  public componentDidUpdate = (prevProps: PlacesAutocompleteInputProps) => {
    if (prevProps.value !== this.props.value) {
      this.setState({ address: this.props.value || '' });
    }
  };

  private googleReadyCallback = () => {
    if (window.google) {
      this.forceUpdate();
      if (this.timerHandler) {
        clearInterval(this.timerHandler);
      }
    }
  };

  private handleInputChange = (address: string) => {
    this.setState({ address }, () => {
      const { onAddressChange } = this.props;
      if (onAddressChange) {
        onAddressChange(address);
      }
    });
  };

  private handleAddressSelect = (address: string) => {
    Analytics.track('Google Autocomplete Address Selected');
    this.setState({ address }, () => {
      const { onAddressSelect } = this.props;
      if (onAddressSelect) {
        onAddressSelect(address);
      }
    });
  };

  public render() {
    const { address } = this.state;
    const {
      bounds,
      className,
      value,
      startAdornment,
      endAdornment,
      onAddressSelect,
      onAddressChange,
      googleCallbackName,
      restrictions,
      enableHintPopup = false,
      portal = false,
      ...otherProps
    } = this.props;

    if (!window.google) {
      console.info('Google Maps API was not loaded');
      return <div>Carregando Buscador de Endereço...</div>;
    }

    // This ensure that we'll only ask Google for geocoding data
    const searchOptionsTypes = ['geocode', 'establishment'];

    const suggestionsList = (suggestions: any, getSuggestionItemProps: any) => (
      <ul className={styles.list}>
        {suggestions.map((suggestion) => {
          const suggestionClass = `${styles.suggestion} ${suggestion.active && styles.active}`;
          return (
            <li
              key={suggestion.placeId + suggestion.index}
              {...getSuggestionItemProps(suggestion, {
                className: suggestionClass,
              })}
            >
              <LocationIcon className={styles.icon} />
              <HintPopup arrow placement='right' title={suggestion.description} enabled={enableHintPopup}>
                <span className={styles.description}>{suggestion.description}</span>
              </HintPopup>
            </li>
          );
        })}
      </ul>
    );

    const portalContainer = document.getElementById(portalContainerId);

    return (
      <>
        <PlacesAutocomplete
          value={this.state.address}
          onChange={this.handleInputChange}
          onSelect={this.handleAddressSelect}
          googleCallbackName={googleCallbackName}
          debounce={this.props.customAutoCompleteDebounceTime || AUTOCOMPLETE_DEBOUNCE_TIME_IN_MILLIS}
          shouldFetchSuggestions={this.props.alwaysFetchSuggestions || this.state.address.length >= MIN_ADDRESS_LENGTH_TO_AUTOCOMPLETE}
          searchOptions={{
            bounds,
            types: searchOptionsTypes,
            componentRestrictions: restrictions,
          }}
        >
          {({ getInputProps, getSuggestionItemProps, suggestions }) => (
            <div className={cn(styles.wrapper, className)}>
              <DefaultInput
                startAdornment={startAdornment}
                endAdornment={endAdornment}
                {...getInputProps({
                  ...otherProps,
                  className: styles.input,
                })}
              />

              {address && (portal ? createPortal(suggestionsList(suggestions, getSuggestionItemProps), portalContainer) : suggestionsList(suggestions, getSuggestionItemProps))}
            </div>
          )}
        </PlacesAutocomplete>
        {portal && <div id={portalContainerId} />}
      </>
    );
  }
}
